"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "use-intl";

import { NotificationIcon, SellIcon, SyncIcon } from "@/components/appIcons";
import { Link } from "@/components/link";
import { Button } from "@/components/ui/button";
import { getNotificationsInfiniteQueryOptions } from "@/routes/_app/notifications/notifications-query";
import { client } from "@/utils/orpc.client";

import { NotificationItem } from "./notification-item";
import { getNotificationPreviewQueryOptions } from "./notification-preview-query";
import { clearUnreadNotificationsCount } from "./unread-count-query";

interface NotificationDropdownProps {
  onClose: () => void;
}

export function NotificationDropdown({ onClose }: NotificationDropdownProps) {
  const t = useTranslations("globals.notifications");
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(getNotificationPreviewQueryOptions());

  const markAllMutation = useMutation({
    mutationFn: () => client.notifications.markAllRead(),
    onSuccess: () => {
      clearUnreadNotificationsCount(queryClient);
      queryClient.invalidateQueries({
        queryKey: getNotificationPreviewQueryOptions().queryKey,
      });
      queryClient.invalidateQueries({ queryKey: getNotificationsInfiniteQueryOptions().queryKey });
    },
  });

  const notifications = data?.items ?? [];
  const hasUnread = notifications.some((n) => !n.read);

  return (
    <div className="absolute right-0 top-full mt-2 w-[380px] rounded-lg border border-border bg-card shadow-lg z-50">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="font-semibold text-sm">{t("title")}</h3>
        {hasUnread && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => markAllMutation.mutate()}
            disabled={markAllMutation.isPending}
            className="text-xs h-auto py-1 px-2"
          >
            {t("markAllRead")}
          </Button>
        )}
      </div>

      <div className="max-h-[400px] overflow-y-auto">
        {isLoading && (
          <div className="flex justify-center py-6" role="status" aria-label={t("loading")}>
            <SyncIcon className="animate-spin text-xl text-primary" decorative />
          </div>
        )}

        {!isLoading && notifications.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6">{t("empty")}</p>
        )}

        {notifications.map((notification) => (
          <NotificationItem key={notification.id} notification={notification} />
        ))}
      </div>

      <div className="grid gap-1 border-t border-border p-2">
        <Link
          to={"/my-offers"}
          onClick={onClose}
          className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <SellIcon className="text-[18px] text-primary" decorative />
          <span>{t("viewMyOffers")}</span>
        </Link>
        <Link
          to={"/notifications"}
          onClick={onClose}
          className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <NotificationIcon className="text-[18px] text-primary" decorative />
          <span>{t("viewAll")}</span>
        </Link>
      </div>
    </div>
  );
}
